// Pure helpers behind the moderator's test queue and validation contracts.
// Extracted from useGetPaid as is: same field fallbacks, same filters.
import { ContractStatus, ContractTitle } from "@/lib/types/contracts";
import type { ModeratorGetPaidTab } from "@/components/get-paid";
import { isValidatedByCurrentModerator } from "../helpers";

/** `?section=` values the moderator links use, mapped onto contract tabs. */
export const SECTION_STATUS: Record<string, ContractStatus> = {
  draft: ContractStatus.DRAFT,
  sent: ContractStatus.SENT,
  viewed: ContractStatus.VIEWED,
  signed: ContractStatus.SIGNED,
  final_validated: ContractStatus.FINAL_VALIDATED,
};

export const NAV_TAB_STATUS: Record<Exclude<ModeratorGetPaidTab, "pending">, ContractStatus> = {
  draft: ContractStatus.DRAFT,
  sent: ContractStatus.SENT,
  viewed: ContractStatus.VIEWED,
  signed: ContractStatus.SIGNED,
  final_validated: ContractStatus.FINAL_VALIDATED,
};

export function moderatorNavTabFor(activeModTab: "PENDING" | "VALIDATED", contractTab: ContractStatus): ModeratorGetPaidTab {
  if (activeModTab === "PENDING") return "pending";
  const mapping: Record<string, ModeratorGetPaidTab> = {
    [ContractStatus.DRAFT]: "draft",
    [ContractStatus.SENT]: "sent",
    [ContractStatus.VIEWED]: "viewed",
    [ContractStatus.SIGNED]: "signed",
    [ContractStatus.FINAL_VALIDATED]: "final_validated",
  };
  return mapping[contractTab] ?? "draft";
}

/** A string score is parsed, anything not finite becomes null. */
export function parseScore(raw: unknown): number | null {
  const parsed = typeof raw === "string" ? parseFloat(raw) : raw;
  return Number.isFinite(parsed) ? (parsed as number) : null;
}

const embeddedContract = (it: any) =>
  it.contract
    ? {
        id: it.contract.id,
        status: String(it.contract.status || "").toLowerCase(),
        signedAt: it.contract.signedAt || null,
        createdAt: it.contract.createdAt,
        updatedAt: it.contract.updatedAt,
      }
    : null;

export interface SubmissionCopy {
  userTestTitle: string;
  languageTestSubmission: string;
}

/** A submission waiting for correction, from `getPendingSubmissions`. */
export function normalizePendingSubmission(it: any) {
  return {
    id: it.id,
    userId: it.userId || it.user?.id,
    userName: it.user?.name || it.userName || it.userId || "",
    language: it.language || it.test?.language || it.Test?.language,
    submittedAt: it.createdAt,
    status: String(it.status || "PENDING").toUpperCase(),
    correctionStatus: String(it.correctionStatus || "TO_CORRECT").toUpperCase(),
    correctedAt: it.correctedAt || null,
    totalScore: parseScore(it.earnedPoints ?? it.totalScore ?? null),
    title: it.title || "Test utilisateur",
    summary: it.summary || "Soumission de test de langue",
    createdAt: it.createdAt,
    answers: it.answers,
  };
}

/** Pending first; the comparator is kept from the original, even if lopsided. */
export function pendingQueue(items: any[]) {
  const pendingOnly = items
    .map(normalizePendingSubmission)
    .filter((item) => item.status === "PENDING" && item.correctionStatus !== "CORRECTED");
  pendingOnly.sort((a, b) => (a.status === "PENDING" && b.status !== "PENDING" ? -1 : 1));
  return pendingOnly;
}

/** A final-validated submission (the "already finalized" tab). */
export function normalizeValidatedSubmission(it: any, copy: SubmissionCopy, currentUser: unknown) {
  return {
    id: it.id,
    userId: it.userId || it.user?.id,
    userName: it.user?.name || it.userName || it.userId || "",
    language: it.language || it.test?.language || it.Test?.language,
    submittedAt: it.createdAt,
    status: String(it.status || "VALIDATED").toUpperCase(),
    correctionStatus: String(it.correctionStatus || "CORRECTED").toUpperCase(),
    correctedAt: it.correctedAt || it.updatedAt || it.createdAt,
    totalScore: parseScore(it.score ?? it.earnedPoints ?? it.totalScore ?? null),
    finalValidatedByCurrentUser: isValidatedByCurrentModerator(it, currentUser),
    title: it.title || copy.userTestTitle,
    summary: it.summary || copy.languageTestSubmission,
    createdAt: it.createdAt,
    answers: it.answers,
    contract: embeddedContract(it),
  };
}

/** A corrected submission, waiting on its contract. */
export function normalizeCorrectedSubmission(it: any, copy: SubmissionCopy) {
  return {
    id: it.id,
    userId: it.userId || it.user?.id,
    userName: it.user?.name || it.userName || it.userId || "",
    language: it.language || it.test?.language || it.Test?.language,
    submittedAt: it.createdAt,
    status: String(it.status || "PENDING").toUpperCase(),
    correctionStatus: String(it.correctionStatus || "TO_CORRECT").toUpperCase(),
    correctedAt: it.correctedAt || it.updatedAt || null,
    totalScore: parseScore(it.score ?? it.earnedPoints ?? it.totalScore ?? null),
    title: it.title || copy.userTestTitle,
    summary: it.summary || copy.languageTestSubmission,
    createdAt: it.createdAt,
    answers: it.answers,
    contract: embeddedContract(it),
  };
}

/** The "draft" tab also holds tests with no contract yet. */
export function matchesContractTab(test: any, tab: ContractStatus | string) {
  const itemContractStatus = String(test?.contract?.status || "").trim().toLowerCase();
  if (tab === ContractStatus.DRAFT) {
    return !itemContractStatus || itemContractStatus === ContractStatus.DRAFT;
  }
  return itemContractStatus === tab;
}

/**
 * Corrected submissions for one language and contract tab, deduplicated by id
 * (a copy carrying a contract wins). Not sorted: the caller sorts by score.
 */
export function correctedForTab(items: any[], copy: SubmissionCopy, language: string, contractStatus: ContractStatus) {
  const merged = new Map<string, any>();
  items
    .map((it) => normalizeCorrectedSubmission(it, copy))
    .filter((item) => {
      const matchesLanguage = item.language === language;
      const shouldBeValidated = contractStatus === ContractStatus.FINAL_VALIDATED;
      const matchesTestStatus = shouldBeValidated ? item.status === "VALIDATED" : item.status === "PENDING";
      return item.correctionStatus === "CORRECTED" && matchesLanguage && matchesContractTab(item, contractStatus) && matchesTestStatus;
    })
    .forEach((item) => {
      const existing = merged.get(String(item.id));
      if (!existing) {
        merged.set(String(item.id), item);
        return;
      }
      merged.set(String(item.id), item.contract && !existing.contract ? item : { ...existing, ...item, contract: existing.contract || item.contract });
    });
  return Array.from(merged.values());
}

const isPendingIn = (test: any, language: string) =>
  String(test?.status || "").toUpperCase() === "PENDING" && test.language === language;

export const visiblePending = (pendingTests: any[], language: string) =>
  pendingTests.filter((test) => isPendingIn(test, language));

/** What the corrected list shows for a tab; final-validated reads its own list. */
export function visibleCorrected(correctedTests: any[], validatedTests: any[], language: string, tab: ContractStatus) {
  const source = tab === ContractStatus.FINAL_VALIDATED ? validatedTests : correctedTests;
  return source.filter((test) => test.language === language && matchesContractTab(test, tab));
}

/** Badge counts of the moderator nav. */
export function sectionCount(
  status: ContractStatus | "pending",
  lists: { pendingTests: any[]; correctedTests: any[]; validatedTests: any[] },
  language: string,
) {
  if (status === "pending") return lists.pendingTests.filter((test) => isPendingIn(test, language)).length;
  if (status === ContractStatus.FINAL_VALIDATED) {
    return lists.validatedTests.filter((test) => test.language === language).length;
  }
  return lists.correctedTests.filter((test) => test.language === language && matchesContractTab(test, status)).length;
}

const contractTime = (contract: any) => new Date(contract?.updatedAt || contract?.createdAt || 0).getTime();
const isValidationContract = (contract: any) =>
  String(contract?.title || "").trim().toUpperCase() === ContractTitle.VALIDATION_CONTRACT;

/** The newest validation contract of the user in one of the given statuses. */
export function latestValidationContract(contracts: any[], statuses: ContractStatus[]) {
  return [...contracts]
    .filter((contract) => {
      const status = String(contract?.status || "").trim().toLowerCase();
      return isValidationContract(contract) && statuses.includes(status as ContractStatus);
    })
    .sort((a, b) => contractTime(b) - contractTime(a))[0] ?? null;
}

const assignedUserOf = (contract: any) => contract?.assignedTo?.id ?? contract?.assignedToId ?? contract?.assignedTo;

const SENT_OR_LATER = [ContractStatus.SENT, ContractStatus.VIEWED, ContractStatus.SIGNED, ContractStatus.FINAL_VALIDATED];
const SIGNED_OR_LATER = [ContractStatus.SIGNED, ContractStatus.FINAL_VALIDATED];

export interface LocalContractProgress {
  contractId?: string;
  userId?: string;
  sent: boolean;
  signed: boolean;
  finalValidated: boolean;
}

export interface ContractProgress {
  contractId?: string;
  sent: boolean;
  signed: boolean;
  finalValidated: boolean;
}

/** Contract progress per test id, from the newest contract of each user. */
export function contractProgressByTest(
  moderatorContracts: any[],
  tests: any[],
  localProgress: Record<string, LocalContractProgress>,
): Record<string, ContractProgress> {
  const contractsByUser = new Map<string, any>();
  moderatorContracts.forEach((contract) => {
    const assignedUserId = assignedUserOf(contract);
    if (!isValidationContract(contract) || assignedUserId === undefined || assignedUserId === null) return;
    const key = String(assignedUserId);
    const existing = contractsByUser.get(key);
    if (!existing || contractTime(contract) >= contractTime(existing)) {
      contractsByUser.set(key, contract);
    }
  });

  const progressByTest: Record<string, ContractProgress> = {};
  tests.forEach((test) => {
    const local = localProgress[String(test.id)];
    const contract = contractsByUser.get(String(test?.userId || ""));
    const embedded = test?.contract;
    const status = String(contract?.status || embedded?.status || "").trim().toLowerCase() as ContractStatus;
    progressByTest[String(test.id)] = {
      contractId: contract?.id || embedded?.id || local?.contractId,
      sent: SENT_OR_LATER.includes(status) || Boolean(local?.sent),
      signed: SIGNED_OR_LATER.includes(status) || Boolean(local?.signed),
      finalValidated: status === ContractStatus.FINAL_VALIDATED || Boolean(local?.finalValidated),
    };
  });
  return progressByTest;
}

/**
 * Local progress refreshed from the fetched contracts. Returns `prev` itself
 * when nothing changed, so a state update is a no-op.
 */
export function syncLocalContractProgress(
  prev: Record<string, LocalContractProgress>,
  tests: any[],
  moderatorContracts: any[],
): Record<string, LocalContractProgress> {
  let changed = false;
  const next = { ...prev };
  tests.forEach((test) => {
    const contract = moderatorContracts.find(
      (item) => isValidationContract(item) && String(assignedUserOf(item) || "") === String(test?.userId || ""),
    );
    if (!contract) return;
    const status = String(contract?.status || "").trim().toLowerCase() as ContractStatus;
    const nextValue = {
      contractId: String(contract?.id || prev[String(test.id)]?.contractId || ""),
      userId: String(test?.userId || prev[String(test.id)]?.userId || ""),
      sent: SENT_OR_LATER.includes(status),
      signed: SIGNED_OR_LATER.includes(status),
      finalValidated: status === ContractStatus.FINAL_VALIDATED,
    };
    const current = prev[String(test.id)];
    if (
      !current ||
      current.contractId !== nextValue.contractId ||
      current.sent !== nextValue.sent ||
      current.signed !== nextValue.signed ||
      current.finalValidated !== nextValue.finalValidated
    ) {
      next[String(test.id)] = nextValue;
      changed = true;
    }
  });
  return changed ? next : prev;
}
